import { useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'

import {
  completions,
  formulaTokens,
  functionName,
  signatureAt,
  type Suggestion,
  type Token,
} from '@/features/dashboard/lib/formula'
import type { Check, Vocabulary } from '@/features/mitra/api/dashboard'
import { cn } from '@/lib/utils'

/**
 * The formula box: a plain textarea the reader types in, over a coloured copy
 * of what they typed, with the names they can use a tap away.
 *
 * COLOUR IS THE ONLY THING DECIDED HERE. A name is coloured by what kind of
 * number it is (rupees, percent, ratio, shares), a name the dashboard does not
 * know is underlined, and the spans the engine flagged are underlined where it
 * says. Whether the formula reads, and what it is worth, is the engine's answer
 * under the box (`FormulaCheck`); nothing here evaluates.
 *
 * A PHONE'S KEYBOARD HAS NO BRACKETS ON ITS FIRST PAGE, so the insert bar
 * carries them; a typed "(" closes itself, which ends most bracket errors
 * before the engine sees them; and a sector filter is picked from the reader's
 * own sectors rather than spelt.
 */

/** The same box metrics for the textarea and the coloured copy behind it:
 *  if these differ by a pixel the colours drift off the letters. */
const BOX =
  'w-full rounded-md border px-3 py-2 font-mono text-body-s leading-relaxed whitespace-pre-wrap break-words'

const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'

const KEYWORDS = new Set(['and', 'or', 'not'])

const UNIT_CLASS: Record<string, string> = {
  NPR: 'text-tone-teal',
  percent: 'text-tone-info',
  ratio: 'text-tone-violet',
  shares: 'text-foreground font-semibold',
  count: 'text-foreground font-semibold',
  text: 'text-tone-saffron',
}

const CHIP = cn(
  'min-h-9 shrink-0 rounded-full border border-border bg-card px-3 py-2 font-mono text-body-s text-foreground hover:bg-muted',
  FOCUS,
)

/** What an insert-bar chip puts in, and where the caret goes after. */
interface Insert {
  label: string
  text: string
  caret?: number
  aria?: string
}

const OPERATORS: Insert[] = [
  { label: '( )', text: '()', caret: 1, aria: '( )' },
  { label: ')', text: ')' },
  { label: '+', text: ' + ' },
  { label: '−', text: ' - ', aria: '-' },
  { label: '×', text: ' * ', aria: '*' },
  { label: '÷', text: ' / ', aria: '/' },
  { label: '==', text: ' == ' },
  { label: "' '", text: "''", caret: 1, aria: "'" },
  { label: ',', text: ', ' },
]

const FREQUENT = ['value', 'quantity', 'wacc', 'ltp', 'cost', 'gain', 'eps']

interface Span {
  start: number
  end: number
  tone: 'error' | 'warning'
}

function classOf(token: Token, next: Token | undefined, vocabulary: Vocabulary | null): string {
  switch (token.kind) {
    case 'number':
      return 'text-foreground'
    case 'text':
      return 'text-tone-saffron'
    case 'op':
    case 'open':
    case 'close':
    case 'comma':
      return 'text-muted-foreground'
    case 'name': {
      const word = token.text.toLowerCase()
      if (KEYWORDS.has(word)) return 'text-muted-foreground italic'
      if (next?.kind === 'open' || next?.kind === 'space') {
        if (vocabulary?.functions.some((f) => functionName(f.name) === word)) {
          return 'text-foreground font-semibold'
        }
      }
      const variable = vocabulary?.variables.find((v) => v.name === word)
      if (variable) return UNIT_CLASS[variable.unit] ?? 'text-foreground'
      if (!vocabulary) return 'text-foreground'
      return 'text-destructive underline decoration-wavy decoration-destructive'
    }
    default:
      return 'text-foreground'
  }
}

/** The formula as coloured runs, cut wherever the engine's error or warning
 *  spans begin or end so the underline covers just them. */
function runs(value: string, vocabulary: Vocabulary | null, spans: Span[]) {
  const tokens = formulaTokens(value)
  const cuts = new Set<number>([0, value.length])
  for (const t of tokens) {
    cuts.add(t.start)
    cuts.add(t.end)
  }
  for (const s of spans) {
    cuts.add(Math.max(0, Math.min(s.start, value.length)))
    cuts.add(Math.max(0, Math.min(s.end, value.length)))
  }
  const points = [...cuts].sort((a, b) => a - b)
  const out: { text: string; className: string; key: number }[] = []
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i] ?? 0
    const end = points[i + 1] ?? 0
    if (start === end) continue
    const index = tokens.findIndex((t) => t.start <= start && t.end >= end)
    const token = tokens[index]
    const next = tokens.slice(index + 1).find((t) => t.kind !== 'space')
    let className = token ? classOf(token, next, vocabulary) : 'text-foreground'
    const hit = spans.find((s) => s.start <= start && s.end >= end && s.end > s.start)
    if (hit?.tone === 'error') className += ' underline decoration-destructive decoration-2'
    else if (hit?.tone === 'warning')
      className += ' underline decoration-warning decoration-dotted decoration-2'
    out.push({ text: value.slice(start, end), className, key: start })
  }
  return out
}

export default function FormulaInput({
  id,
  value,
  onChange,
  vocabulary,
  check,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  vocabulary: Vocabulary | null
  check: Check | null
}) {
  const area = useRef<HTMLTextAreaElement>(null)
  const shadow = useRef<HTMLPreElement>(null)
  const caretAfter = useRef<number | null>(null)
  const [caret, setCaret] = useState(value.length)
  const [active, setActive] = useState(0)
  const [dismissed, setDismissed] = useState(false)
  const [sheet, setSheet] = useState<'none' | 'sector' | 'more'>('none')
  const [search, setSearch] = useState('')
  const listId = useId()

  // A caret placed by an insert lands after React has written the value.
  useLayoutEffect(() => {
    if (caretAfter.current === null || !area.current) return
    const at = caretAfter.current
    caretAfter.current = null
    area.current.setSelectionRange(at, at)
    // A caret placed by code is first knowable here, after the layout pass.
    setCaret(at)
  }, [value])

  const suggested = useMemo(
    () => (vocabulary && !dismissed ? completions(value, caret, vocabulary) : null),
    [value, caret, vocabulary, dismissed],
  )
  const signature = useMemo(
    () => (vocabulary ? signatureAt(value, caret, vocabulary) : null),
    [value, caret, vocabulary],
  )

  const spans: Span[] = []
  if (check?.error) {
    const start = check.error.position
    spans.push({ start, end: Math.max(check.error.end, start + 1), tone: 'error' })
  }
  for (const w of check?.warnings ?? []) {
    // A warning about the whole formula (a unit, a shape) underlines nothing:
    // it is said in words under the box instead.
    if (w.end > w.position && w.end - w.position < value.length) {
      spans.push({ start: w.position, end: w.end, tone: 'warning' })
    }
  }

  function replace(start: number, end: number, text: string, caretOffset = text.length) {
    caretAfter.current = start + caretOffset
    setDismissed(false)
    setActive(0)
    onChange(value.slice(0, start) + text + value.slice(end))
  }

  function insert(item: Insert) {
    const el = area.current
    const start = el?.selectionStart ?? caret
    const end = el?.selectionEnd ?? caret
    replace(start, end, item.text, item.caret ?? item.text.length)
    el?.focus()
  }

  function accept(item: Suggestion) {
    if (!suggested) return
    if (item.isFunction) {
      replace(suggested.start, suggested.end, `${item.insert}()`, item.insert.length + 1)
    } else {
      replace(suggested.start, suggested.end, item.insert)
    }
    area.current?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    const el = event.currentTarget
    const items = suggested?.items ?? []
    if (items.length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setActive((a) => (a + 1) % items.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setActive((a) => (a - 1 + items.length) % items.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        const item = items[Math.min(active, items.length - 1)]
        if (item) accept(item)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setDismissed(true)
        return
      }
    }
    // A bracket closes itself, and typing the closer over one already there
    // steps past it: a code editor's habit, and the end of most bracket errors.
    if (event.key === '(' && el.selectionStart === el.selectionEnd) {
      event.preventDefault()
      replace(el.selectionStart, el.selectionEnd, '()', 1)
      return
    }
    if (
      event.key === ')' &&
      el.selectionStart === el.selectionEnd &&
      value[el.selectionStart] === ')'
    ) {
      event.preventDefault()
      const past = el.selectionStart + 1
      el.setSelectionRange(past, past)
      setCaret(past)
      return
    }
    // One line: Enter never breaks the formula.
    if (event.key === 'Enter') event.preventDefault()
  }

  const shown = runs(value, vocabulary, spans)
  const open = suggested !== null && suggested.items.length > 0
  const query = search.trim().toLowerCase()
  const groups = useMemo(() => {
    const map = new Map<string, Vocabulary['variables']>()
    for (const v of vocabulary?.variables ?? []) {
      if (query && !`${v.name} ${v.meaning}`.toLowerCase().includes(query)) continue
      map.set(v.group, [...(map.get(v.group) ?? []), v])
    }
    return [...map]
  }, [vocabulary, query])
  const functions = (vocabulary?.functions ?? []).filter(
    (f) => !query || `${f.name} ${f.meaning}`.toLowerCase().includes(query),
  )
  const sectors = vocabulary?.held.sector ?? []

  return (
    <div data-role="formula-input" className="flex min-w-0 flex-col gap-2">
      <div className="relative">
        <pre
          ref={shadow}
          aria-hidden="true"
          className={cn(
            BOX,
            'pointer-events-none absolute inset-0 m-0 overflow-hidden border-transparent bg-card',
          )}
        >
          {shown.map((run) => (
            <span key={run.key} className={run.className}>
              {run.text}
            </span>
          ))}{' '}
        </pre>
        <textarea
          ref={area}
          id={id}
          value={value}
          rows={2}
          maxLength={400}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open ? `${listId}-${String(active)}` : undefined}
          onChange={(e) => {
            setDismissed(false)
            setActive(0)
            setCaret(e.target.selectionStart)
            onChange(e.target.value)
          }}
          onSelect={(e) => {
            setCaret(e.currentTarget.selectionStart)
          }}
          onKeyDown={onKeyDown}
          onScroll={(e) => {
            if (shadow.current) shadow.current.scrollTop = e.currentTarget.scrollTop
          }}
          className={cn(
            BOX,
            'relative block resize-none border-border bg-transparent text-transparent caret-foreground',
            'selection:bg-tone-info-soft',
            FOCUS,
          )}
        />
      </div>

      {signature ? (
        <p data-role="signature" className="font-mono text-body-xs text-muted-foreground">
          {functionName(signature.signature)}(
          {signature.params.map((param, index) => (
            <span key={param}>
              {index > 0 ? ', ' : null}
              {index === signature.index ? (
                <b data-role="signature-arg" className="font-semibold text-foreground">
                  {param}
                </b>
              ) : (
                param
              )}
            </span>
          ))}
          ) <span className="font-sans">— {signature.meaning}</span>
        </p>
      ) : null}

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Suggestions"
          className="flex flex-col overflow-hidden rounded-md border border-border bg-popover"
        >
          {suggested.items.map((item, index) => (
            <li
              key={item.label}
              id={`${listId}-${String(index)}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
              onClick={() => {
                accept(item)
              }}
              className={cn(
                'flex min-h-10 cursor-pointer flex-col justify-center px-3 py-1',
                index === active ? 'bg-muted' : '',
              )}
            >
              <span className="flex items-baseline gap-2">
                <span className="font-mono text-body-s text-foreground">{item.label}</span>
                {item.unit ? (
                  <span className="text-label-s text-muted-foreground uppercase">{item.unit}</span>
                ) : null}
                {item.sample ? (
                  <span className="ml-auto font-mono text-body-xs text-muted-foreground">
                    {item.sample}
                  </span>
                ) : null}
              </span>
              <span className="truncate text-body-xs text-muted-foreground">{item.detail}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div
        role="toolbar"
        aria-label="Insert into the formula"
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        {OPERATORS.map((item) => (
          <button
            key={item.label}
            type="button"
            className={CHIP}
            aria-label={`Insert ${item.aria ?? item.label}`}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={() => {
              insert(item)
            }}
          >
            {item.label}
          </button>
        ))}
        {FREQUENT.map((name) => (
          <button
            key={name}
            type="button"
            className={CHIP}
            aria-label={`Insert ${name}`}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={() => {
              insert({ label: name, text: name })
            }}
          >
            {name}
          </button>
        ))}
        {sectors.length > 0 ? (
          <button
            type="button"
            className={CHIP}
            aria-expanded={sheet === 'sector'}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={() => {
              setSheet(sheet === 'sector' ? 'none' : 'sector')
            }}
          >
            sector =
          </button>
        ) : null}
        <button
          type="button"
          className={CHIP}
          aria-expanded={sheet === 'more'}
          onMouseDown={(e) => {
            e.preventDefault()
          }}
          onClick={() => {
            setSheet(sheet === 'more' ? 'none' : 'more')
          }}
        >
          More
        </button>
      </div>

      {sheet === 'sector' ? (
        <div
          data-role="sector-picker"
          role="group"
          aria-label="Choose one of your sectors"
          className="flex flex-wrap gap-2 rounded-md border border-border bg-card p-2"
        >
          {sectors.map((sector) => (
            <button
              key={sector}
              type="button"
              className={CHIP}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
              onClick={() => {
                insert({ label: sector, text: `sector == '${sector}'` })
                setSheet('none')
              }}
            >
              {sector}
            </button>
          ))}
        </div>
      ) : null}

      {sheet === 'more' ? (
        <div
          data-role="names-sheet"
          className="flex max-h-72 flex-col gap-2 overflow-y-auto rounded-md border border-border bg-card p-2"
        >
          <input
            type="search"
            value={search}
            aria-label="Search names"
            placeholder="Search names"
            onChange={(e) => {
              setSearch(e.target.value)
            }}
            className={cn(
              'w-full rounded-md border border-border bg-background px-3 py-2 text-body-s text-foreground',
              FOCUS,
            )}
          />
          {groups.map(([group, variables]) => (
            <div key={group}>
              <p className="text-label-s text-muted-foreground uppercase">{group}</p>
              <ul className="mt-1 flex flex-col">
                {variables.map((v) => {
                  const sample = vocabulary?.samples[v.name]
                  return (
                    <li key={v.name}>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault()
                        }}
                        onClick={() => {
                          insert({ label: v.name, text: v.name })
                        }}
                        className={cn(
                          'flex min-h-10 w-full flex-col rounded-sm px-2 py-1 text-left hover:bg-muted',
                          FOCUS,
                        )}
                      >
                        <span className="flex items-baseline gap-2">
                          <span
                            className={cn(
                              'font-mono text-body-s',
                              UNIT_CLASS[v.unit] ?? 'text-foreground',
                            )}
                          >
                            {v.name}
                          </span>
                          {v.unit !== 'text' ? (
                            <span className="text-label-s text-muted-foreground uppercase">
                              {v.unit}
                            </span>
                          ) : null}
                          {sample ? (
                            <span className="ml-auto font-mono text-body-xs text-muted-foreground">
                              {sample.symbol} {sample.value}
                            </span>
                          ) : null}
                        </span>
                        <span className="text-body-xs text-muted-foreground">{v.meaning}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
          {functions.length > 0 ? (
            <div>
              <p className="text-label-s text-muted-foreground uppercase">Functions</p>
              <ul className="mt-1 flex flex-col">
                {functions.map((f) => (
                  <li key={f.name}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                      }}
                      onClick={() => {
                        const name = functionName(f.name)
                        insert({ label: name, text: `${name}()`, caret: name.length + 1 })
                      }}
                      className={cn(
                        'flex min-h-10 w-full flex-col rounded-sm px-2 py-1 text-left hover:bg-muted',
                        FOCUS,
                      )}
                    >
                      <span className="font-mono text-body-s font-semibold text-foreground">
                        {f.name}
                      </span>
                      <span className="text-body-xs text-muted-foreground">{f.meaning}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
